import type { RosterPlayerListItem } from './mapRosterPlayerToForm'

/** Which of the three roster form modes the route params select. */
export type RosterMode = 'create' | 'addExisting' | 'edit'

/**
 * Resolve the form mode from the screen's params. `editPlayer` (a roster row)
 * always means edit, even if a playerId is also present; a bare playerId means
 * add-existing (copy); neither means create-new. Pure — unit-testable.
 */
export const resolveRosterMode = (params: {
  playerId?: string
  editPlayer?: RosterPlayerListItem
}): RosterMode => {
  if (params.editPlayer) {
    return 'edit'
  }
  if (params.playerId) {
    return 'addExisting'
  }
  return 'create'
}

/**
 * Whether the roster player's email field must be locked. Email is the player's
 * settled identity once it's confirmed, so it's read-only when:
 *   - add-existing (copy) — the player's profile is owned elsewhere, OR
 *   - editing a player who has ACCEPTED their invite (is_accepted).
 *
 * An invited-but-unaccepted player (is_invited && !is_accepted) keeps email
 * editable so the inviter can still correct a wrong address. Create-new is
 * always editable. Pure — unit-testable.
 */
export const isRosterEmailLocked = (params: {
  mode: RosterMode
  editPlayer?: RosterPlayerListItem
}): boolean => {
  if (params.mode === 'addExisting') {
    return true
  }
  if (params.mode === 'edit') {
    return Boolean(params.editPlayer?.is_accepted)
  }
  return false
}

/**
 * Whether the Canlan Salesforce ID field must be locked (read-only). The id is
 * owned by Salesforce, so it can only ever be SET while creating a brand-new
 * player; on an existing player (add-existing or edit) it is display-only.
 * Pure — unit-testable.
 */
export const isRosterSalesforceIdLocked = (mode: RosterMode): boolean =>
  mode !== 'create'
